import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { REFERRAL_TEMPLATES_LIST } from "../../../../enums/QueryKeys";
import { fetchReferralTemplates } from "../../../../enums/queryFunction/Fetch";
import { EDIT_REFERRAL_TEMPLATES, DELETE_REFERRAL_TEMPLATES } from "../../../../enums/PermissionTitles";
import useUser from "../../../../hooks/useUser";
import { useSearchParams } from "react-router-dom";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import TemplatesSearchFilter from "../components/TemplatesSearchFilter";
import ReferralsTable from "./ReferralsTable";
import ReferralTemplateDetailsDialog from "./ReferralTemplateDetailsDialog";
import ReferralTemplateFormDialog from "./ReferralTemplateFormDialog";
import DeleteReferralTemplateDialog from "./DeleteReferralTemplateDialog";
import { ANIMAL, MATERIAL } from "./ReferralType";

const STRING_SORT_FIELDS = new Set(["title"]);

function compareTemplates(a, b, orderBy, order) {
  const direction = order === "desc" ? -1 : 1;
  const aValue = a?.[orderBy];
  const bValue = b?.[orderBy];
  if (!aValue && !bValue) return 0;
  if (!aValue) return 1;
  if (!bValue) return -1;
  if (STRING_SORT_FIELDS.has(orderBy)) {
    return direction * String(aValue).localeCompare(String(bValue));
  }
  return direction * (Number(aValue) - Number(bValue));
}

/** Mirrors PrescriptionsTab.jsx, with an added ToggleButtonGroup (Animal/Material) - the referralType URL param is the single source of truth for which kind is being browsed. */
const ReferralsTab = React.forwardRef(function ReferralsTab(props, ref) {
  const { t } = useTranslation();
  const { currentClinic } = useUser();
  const clinicId = currentClinic?.id;
  const [searchQuery, setSearchQuery] = useSearchParams("");
  const titleParam = searchQuery.get("referralQ") ?? "";
  const referralType = searchQuery.get("referralType") || ANIMAL;

  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: [REFERRAL_TEMPLATES_LIST + clinicId, referralType, titleParam],
    queryFn: () => fetchReferralTemplates(clinicId, referralType, titleParam),
    staleTime: Infinity,
  });
  const queryClient = useQueryClient();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isEditingAllowed = isPermissionAllowed(EDIT_REFERRAL_TEMPLATES);
  const isDeletingAllowed = isPermissionAllowed(DELETE_REFERRAL_TEMPLATES);

  const items = React.useMemo(() => (Array.isArray(templates) ? templates : []), [templates]);
  const isSearchActive = titleParam !== "";

  const referralSortParam = searchQuery.get("referralSort") || "";
  const [orderBy, order] = referralSortParam
    ? referralSortParam.split("=")
    : ["title", "asc"];
  const sortedItems = React.useMemo(
    () => [...items].sort((a, b) => compareTemplates(a, b, orderBy, order)),
    [items, orderBy, order],
  );

  function handleSort(field, direction) {
    updateQueryParam("referralSort", `${field}=${direction}`);
  }

  const [detailsTemplateId, setDetailsTemplateId] = React.useState(null);
  const [formState, setFormState] = React.useState(null);
  const [templatePendingDelete, setTemplatePendingDelete] = React.useState(null);

  const detailsTemplate = items.find((item) => item.id === detailsTemplateId) ?? null;

  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => setFormState({ template: null }),
  }));

  const updateQueryParam = React.useCallback(
    (key, value) => {
      setSearchQuery((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchQuery],
  );

  function closeTemplateForm() {
    setFormState(null);
    queryClient.invalidateQueries({ queryKey: [REFERRAL_TEMPLATES_LIST + clinicId] });
  }

  if (error) {
    return <SimpleAlert message="ErrorFetchingWarehouseItemsData" severity="error" />;
  }

  return (
    <>
      <Stack spacing={2} mb={3}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={referralType}
          onChange={(event, value) => value && updateQueryParam("referralType", value)}
          sx={{ alignSelf: "flex-start" }}
        >
          <ToggleButton value={ANIMAL} sx={{ textTransform: "none", px: 2 }}>
            {t("ReferralForAnimal")}
          </ToggleButton>
          <ToggleButton value={MATERIAL} sx={{ textTransform: "none", px: 2 }}>
            {t("ReferralForMaterial")}
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", width: "100%" }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, flex: 1, borderRadius: 3, borderColor: "divider" }}
          >
            <TemplatesSearchFilter
              initialValue={titleParam}
              onSearchChange={(val) => updateQueryParam("referralQ", val)}
            />
          </Paper>
        </Box>
      </Stack>

      <ReferralsTable
        items={sortedItems}
        referralType={referralType}
        isLoading={isLoading}
        emptyMessage={isSearchActive ? "NoReferralTemplatesFoundForGivenQuery" : "NoReferralTemplates"}
        onRowClick={(template) => setDetailsTemplateId(template.id)}
        order={order}
        orderBy={orderBy}
        onSort={handleSort}
      />

      <ReferralTemplateDetailsDialog
        template={detailsTemplate}
        canEdit={isEditingAllowed}
        canDelete={isDeletingAllowed}
        onClose={() => setDetailsTemplateId(null)}
        onEdit={(template) => {
          setDetailsTemplateId(null);
          setFormState({ template });
        }}
        onDelete={(template) => setTemplatePendingDelete(template)}
      />

      {formState && (
        <ReferralTemplateFormDialog
          open={Boolean(formState)}
          onClose={closeTemplateForm}
          clinicId={clinicId}
          initialValues={formState.template}
          defaultReferralType={referralType}
        />
      )}

      <DeleteReferralTemplateDialog
        template={templatePendingDelete}
        clinicId={clinicId}
        onCancel={() => setTemplatePendingDelete(null)}
        onDeleted={() => setDetailsTemplateId(null)}
      />
    </>
  );
});

export default ReferralsTab;
